import endpoints from '@app/src/api/endpoints'
import { FetchKey } from '@app/src/api/fetchHooks'
import { useUpdateResource } from '@app/src/api/updateHooks'
import Select from '@app/src/components/Form/Select/ControlledSelect'
import RouterLink from '@app/src/components/RouterLink'
import TextField from '@app/src/components/Ui/TextField'
import { useAmplitude } from '@app/src/context/AmplitudeContext'
import { useSnackbar } from '@app/src/context/SnackbarContext'
import useErrorNotification from '@app/src/hooks/errorNotification'
import type { Invitation } from '@app/src/types/resourceExplorer'
import {
  AmplitudeTrackingEvents,
  NotificationSeverity,
  PrepareCardAction,
  PrepareCardId,
  Roles,
  TrackingSource,
} from '@app/src/wf-constants'
import { Alert, Stack, Tooltip, Typography } from '@mui/material'
import React from 'react'
import { useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useQueryClient } from '@tanstack/react-query'
import { useDrawer } from '../DrawerContext'
import type { DrawerViewProps } from '../DrawerView'
import DrawerView from '../DrawerView'
import { OwnerRestrictionField } from '../../Form/User/OwnerRestrictionField'
import { normalizeOwnerRestriction } from '@app/src/utils/rolePermissions'
import { useAuthentication } from '@app/src/context/AuthenticationContext'

type DrawerViewInvitationFormProps = {
  invitation?: Invitation
  title?: DrawerViewProps['title']
  source?: TrackingSource
} & Omit<DrawerViewProps, 'title'>

type InvitationPayload = Pick<Invitation, 'name' | 'email' | 'role' | 'isOwnerRestricted'>

const DrawerViewInvitationForm: React.FC<DrawerViewInvitationFormProps> = ({ invitation, source, ...props }) => {
  const { closeDrawer } = useDrawer()
  const { showSnackbar } = useSnackbar()
  const { showErrorNotification } = useErrorNotification()
  const { trackEvent } = useAmplitude()
  const { solution } = useAuthentication().scope
  const { handleSubmit, control, formState, setError, register, setValue, errors, watch } = useForm<InvitationPayload>({
    defaultValues: {
      name: invitation?.name,
      email: invitation?.email,
      role: invitation?.role.toLowerCase(),
      isOwnerRestricted: invitation?.isOwnerRestricted || false,
    },
  })
  const { formatMessage } = useIntl()
  const queryClient = useQueryClient()
  const { mutate, isPending: isSubmitting } = useUpdateResource<InvitationPayload>()

  const selectedRole = watch('role')

  const onSubmit = async (values: InvitationPayload): Promise<void> => {
    const invitation: InvitationPayload = {
      name: values.name,
      email: values.email,
      role: values.role,
      isOwnerRestricted: normalizeOwnerRestriction(values.role, undefined, values.isOwnerRestricted ?? false),
    }
    mutate(
      { url: `${endpoints.invitations}/save`, body: invitation },
      {
        onSuccess: () => {
          showSnackbar({
            message: formatMessage(
              { id: 'notifications.successfulCreateInvitation' },
              { b: (chunks: React.ReactNode) => <b>{chunks}</b> },
            ),
            severity: NotificationSeverity.success,
            disableAutoClose: true,
          })
          if (source === TrackingSource.Dashboard) {
            trackEvent({
              name: AmplitudeTrackingEvents.Dashboard.PrepareForRequest.CardActionCompleted,
              eventProps: { card_id: PrepareCardId.Share, action: PrepareCardAction.InvitationSent },
            })
          }
          queryClient.invalidateQueries({ queryKey: [FetchKey.Invitation] })
          closeDrawer?.()
        },
        onError: error => {
          if (error.isValidationError) {
            error.setFormValidationErrors(setError)
            return
          }
          showErrorNotification({ requestError: error, disableAutoClose: true })
        },
      },
    )
  }

  return (
    <DrawerView
      title={formatMessage({ id: 'resourceCollections.create.colleague' })}
      buttons={[
        {
          label: formatMessage({ id: 'reporting.submit' }),
          variant: 'contained',
          loading: isSubmitting,
          type: 'submit',
          disabled: !formState.isDirty && !formState.isValid && !isSubmitting,
        },
      ]}
      onFormSubmit={handleSubmit(onSubmit)}
      {...props}
    >
      <Stack px={2} spacing={2}>
        {Object.keys(formState.errors)?.length > 0 && (
          <Alert variant="filled" severity="error">
            {formatMessage({ id: 'form.validation.error' })}
          </Alert>
        )}

        <Typography variant="body1">
          {formatMessage(
            { id: 'schemas.invitation.description' },
            {
              link: (
                <RouterLink to="/" target="_blank">
                  app.worldfavor.com
                </RouterLink>
              ),
            },
          )}
        </Typography>

        <TextField
          hoveringLabel
          placeholder={formatMessage({ id: 'textFieldPlaceholders.userName' })}
          fullWidth
          inputRef={register({ required: formatMessage({ id: 'form.validation.required' }) })}
          required
          name="name"
          label={formatMessage({ id: 'schemas.invitation.name' })}
          error={Boolean(errors?.name)}
          type="text"
          helperText={errors?.name?.message}
          onClear={(): void => setValue('name', '')}
        />

        <Tooltip
          arrow
          placement="bottom"
          title={invitation ? formatMessage({ id: 'schemas.invitation.cannotEditEmail' }) : ''}
        >
          <span>
            <TextField
              hoveringLabel
              placeholder={formatMessage({ id: 'textFieldPlaceholders.colleagueEmail' })}
              fullWidth
              inputRef={register({ required: formatMessage({ id: 'form.validation.required' }) })}
              required
              name="email"
              label={formatMessage({ id: 'schemas.invitation.email' })}
              error={Boolean(errors?.email)}
              type="email"
              helperText={errors?.email?.message}
              onClear={(): void => setValue('email', '')}
              // Just disabling the button stops the email from being passed to the backend.
              // Therefore, we need to set the email field to readOnly
              // and grey out the input field when invitation already exists.
              InputProps={{
                readOnly: Boolean(invitation),
              }}
              sx={invitation ? { '& .MuiInputBase-input': { color: 'text.disabled' } } : undefined}
            />
          </span>
        </Tooltip>

        <Select
          hoveringLabel
          name="role"
          fieldLabel={formatMessage({ id: 'schemas.invitation.role' })}
          rules={{ required: formatMessage({ id: 'form.validation.required' }) }}
          control={control}
          required
          enableAutoSelect
          options={[
            { value: 'admin', label: formatMessage({ id: 'roles.admin.name' }) },
            { value: 'contributor', label: formatMessage({ id: 'roles.contributor.name' }) },
            { value: 'viewonly', label: formatMessage({ id: 'roles.viewonly.name' }) },
          ]}
          error={errors?.role?.message}
        />

        <OwnerRestrictionField
          name="isOwnerRestricted"
          selectedRole={selectedRole || Roles.Admin}
          control={control}
          placement="bottom"
          solution={solution}
        />
      </Stack>
    </DrawerView>
  )
}

export default DrawerViewInvitationForm
